import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { NoAccess, RequireArea } from './components/RequireArea';
import { useAuth } from './lib/auth-context';
import { firstAllowedPath } from './lib/permissions';
import { AssistantPage } from './pages/Assistant';
import { AuditLogPage } from './pages/AuditLog';
import { ContactDetailPage } from './pages/ContactDetail';
import { ContactsPage } from './pages/Contacts';
import { DashboardPage } from './pages/Dashboard';
import { EinvoicePage } from './pages/Einvoice';
import { InvoiceDetailPage } from './pages/InvoiceDetail';
import { InvoiceEditorPage } from './pages/InvoiceEditor';
import { PurchasesPage } from './pages/Purchases';
import { ExpensesPage } from './pages/Expenses';
import { AccountingPage } from './pages/Accounting';
import { TrialBalancePage } from './pages/TrialBalance';
import { VatReturnPage } from './pages/VatReturn';
import { ReportsPage } from './pages/Reports';
import { AssetsPage } from './pages/Assets';
import { InvoicesPage } from './pages/Invoices';
import { SubscriptionsPage } from './pages/Subscriptions';
import { SnakIntegrationPage } from './pages/SnakIntegration';
import { ProjectsPage } from './pages/Projects';
import { BankingPage } from './pages/Banking';
import { CompliancePage } from './pages/Compliance';
import { ItemsPage } from './pages/Items';
import { LoginPage } from './pages/Login';
import { ProfilePage } from './pages/Profile';
import { SettingsPage } from './pages/Settings';
import { UsersPage } from './pages/Users';

/**
 * Kök rota SABİT DEĞİL.
 *
 * `dashboard` yetkisi olmayan bir kullanıcı (örn. yalnız `audit-log` verilmiş
 * bir denetçi) giriş sonrası boş ekrana düşmemeli; erişebildiği ilk sayfaya
 * gider. Hiçbir yetkisi yoksa döngü yerine açık bir mesaj görür.
 */
function HomeGate() {
  const { user } = useAuth();
  if (user && user.areas.includes('dashboard')) return <DashboardPage />;
  const fallback = firstAllowedPath(user);
  if (!fallback || fallback === '/') return <NoAccess />;
  return <Navigate to={fallback} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<Layout />}>
        <Route index element={<HomeGate />} />

        {/* Profil her oturum sahibinindir — alan izni gerektirmez. */}
        <Route path="/profile" element={<ProfilePage />} />

        {/*
         * M1 rotaları.
         *
         * Katalog alanı `invoices` — `items` DEĞİL. Sunucuda ItemsController
         * `@AreaAuth('invoices')` taşıyor (items.controller.ts:33): katalog
         * kalemi faturanın girdisidir, ayrı bir yetki alanı değildir. Panelde
         * başka bir alan yazmak, menüde görünen ama her isteği 403 dönen bir
         * sayfa demek olurdu.
         *
         * `/invoices/new` ile `/invoices/:id` AYNI bileşeni kullanır; editör
         * `useParams().id` tanımsızken YENİ kipe geçer. Statik segment
         * react-router sıralamasında dinamik olanı zaten yener, ama sıra yine
         * de okunaklı kalsın diye önce yazıldı.
         */}
        <Route
          path="/contacts"
          element={
            <RequireArea area="contacts">
              <ContactsPage />
            </RequireArea>
          }
        />
        <Route
          path="/contacts/:id"
          element={
            <RequireArea area="contacts">
              <ContactDetailPage />
            </RequireArea>
          }
        />
        <Route
          path="/items"
          element={
            <RequireArea area="invoices">
              <ItemsPage />
            </RequireArea>
          }
        />
        <Route
          path="/invoices"
          element={
            <RequireArea area="invoices">
              <InvoicesPage />
            </RequireArea>
          }
        />
        {/*
          ⚠ ABONELİK ALANI `recurring`, `invoices` DEĞİL. Sunucudaki
          SubscriptionsController da `@AreaAuth('recurring')` taşıyor: dönemsel
          sözleşme yönetmek, tek tek fatura kesmekten ayrı bir sorumluluktur ve
          ayrı verilebilmeli. İlk yazımda `invoices` kullanıldı ve hiçbir test
          kırılmadı — kapı sonradan eş anlamlı rota adlarını da tarayacak
          şekilde genişletildi.
        */}
        <Route
          path="/subscriptions"
          element={
            <RequireArea area="recurring">
              <SubscriptionsPage />
            </RequireArea>
          }
        />
        {/*
          ⚠ M17 snak FATURALARI `invoices` alanında: bu ekranın ürettiği şey
          satış faturasıdır ve sunucudaki SnakAdminController da
          `@AreaAuth('invoices')` taşıyor. Alan başına tek iniş sayfası kuralı
          gereği `ROUTE_AREAS`a GİRMEZ (alanın iniş sayfası `/invoices`).
        */}
        <Route
          path="/integrations/snak"
          element={
            <RequireArea area="invoices">
              <SnakIntegrationPage />
            </RequireArea>
          }
        />
        <Route
          path="/projects"
          element={
            <RequireArea area="projects">
              <ProjectsPage />
            </RequireArea>
          }
        />
        <Route
          path="/banking"
          element={
            <RequireArea area="banking">
              <BankingPage />
            </RequireArea>
          }
        />
        <Route
          path="/compliance"
          element={
            <RequireArea area="compliance">
              <CompliancePage />
            </RequireArea>
          }
        />
        <Route
          path="/invoices/new"
          element={
            <RequireArea area="invoices">
              <InvoiceEditorPage />
            </RequireArea>
          }
        />
        <Route
          path="/invoices/:id"
          element={
            <RequireArea area="invoices">
              <InvoiceDetailPage />
            </RequireArea>
          }
        />
        <Route
          path="/invoices/:id/edit"
          element={
            <RequireArea area="invoices">
              <InvoiceEditorPage />
            </RequireArea>
          }
        />

        <Route
          path="/purchases"
          element={
            <RequireArea area="purchases">
              <PurchasesPage />
            </RequireArea>
          }
        />
        <Route
          path="/expenses"
          element={
            <RequireArea area="expenses">
              <ExpensesPage />
            </RequireArea>
          }
        />
        <Route
          path="/accounting"
          element={
            <RequireArea area="accounting">
              <AccountingPage />
            </RequireArea>
          }
        />
        <Route
          path="/accounting/trial-balance"
          element={
            <RequireArea area="accounting">
              <TrialBalancePage />
            </RequireArea>
          }
        />
        <Route
          path="/vat-return"
          element={
            <RequireArea area="vat-return">
              <VatReturnPage />
            </RequireArea>
          }
        />
        <Route
          path="/einvoice"
          element={
            <RequireArea area="einvoice">
              <EinvoicePage />
            </RequireArea>
          }
        />
        <Route
          path="/assets"
          element={
            <RequireArea area="assets">
              <AssetsPage />
            </RequireArea>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireArea area="reports">
              <ReportsPage />
            </RequireArea>
          }
        />

        <Route
          path="/settings"
          element={
            <RequireArea area="settings">
              <SettingsPage />
            </RequireArea>
          }
        />
        <Route
          path="/users"
          element={
            <RequireArea area="users">
              <UsersPage />
            </RequireArea>
          }
        />
        <Route
          path="/audit-log"
          element={
            <RequireArea area="audit-log">
              <AuditLogPage />
            </RequireArea>
          }
        />

        <Route
          path="/assistant"
          element={
            <RequireArea area="assistant">
              <AssistantPage />
            </RequireArea>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
